import { getApiErrorCode, getApiErrorMessage } from './listingAvailability';

/**
 * Refusals from the account-hardening rails (api#131) and the copy the buyer reads.
 *
 * api's own `message` is written for an API client, and for OTP it is downright useless ("Code is
 * not valid" / "OTP not exist"). These are the two facts someone standing in front of the form
 * needs: what went wrong, and what to do next — including how many tries are left before the code
 * burns. Kept pure and separate from the component so it can be unit-tested in the node env (the
 * app has no component-test setup).
 */
export const OTP_INVALID_CODE = 'OTP_INVALID';
export const OTP_EXPIRED_CODE = 'OTP_EXPIRED';
export const EMAIL_CHANGE_REFERENCES_CODE = 'EMAIL_CHANGE_REFERENCES_INVALID';
export const EMAIL_ALREADY_IN_USE_CODE = 'EMAIL_ALREADY_IN_USE';
export const WALLET_SIGNATURE_REQUIRED_CODE = 'WALLET_SIGNATURE_REQUIRED';
export const WALLET_SIGNATURE_INVALID_CODE = 'WALLET_SIGNATURE_INVALID';

/**
 * Tries left on the OTP that was just refused, or `undefined` when the body says nothing.
 *
 * Lives in `payload` — the exception filter lifts only `code` to the top level. Zero is a real
 * answer (the code just burned), so callers must check for `undefined`, not falsiness.
 */
export const otpAttemptsLeftOf = (err: unknown): number | undefined => {
  const attemptsLeft = (err as { payload?: { attemptsLeft?: unknown } })?.payload?.attemptsLeft;

  return typeof attemptsLeft === 'number' ? attemptsLeft : undefined;
};

/**
 * Which OTP a refusal is about, as the reference the request carried. The email change submits two
 * codes at once, so without this the form could only say "one of these is wrong".
 */
export const otpReferenceOf = (err: unknown): string | undefined => {
  const reference = (err as { payload?: { otpReference?: unknown } })?.payload?.otpReference;

  return typeof reference === 'string' ? reference : undefined;
};

/**
 * Human copy for a refused sensitive action (spec §4.4). Falls back to the api message, and then to
 * the caller's own, so an unmapped code is still explained rather than swallowed.
 */
export const securityErrorMessage = (err: unknown, fallback: string): string => {
  switch (getApiErrorCode(err)) {
    case OTP_INVALID_CODE: {
      const attemptsLeft = otpAttemptsLeftOf(err);

      // "Not valid" is the api's own wording and what the sign-in screen is checked against
      // (SF-G1); the count is the part that used to stay in the response body (BUG-13).
      if (attemptsLeft === undefined) {
        return 'That code is not valid. Check it and try again.';
      }

      if (attemptsLeft === 0) {
        return 'That code is not valid and has now been cancelled after too many wrong tries. Request a new one.';
      }

      return attemptsLeft === 1
        ? 'That code is not valid. One more wrong try and the code is cancelled.'
        : `That code is not valid. ${attemptsLeft} attempts left before the code is cancelled.`;
    }
    case OTP_EXPIRED_CODE:
      // One code for expired, already used, and burned after five wrong tries — deliberately
      // indistinguishable on the api side, and the way out is the same for all three.
      return 'That code is no longer valid — it expired, was already used, or was cancelled after too many wrong tries. Start again to get a new one.';
    case EMAIL_CHANGE_REFERENCES_CODE:
      return 'These codes do not belong to the same email change. Start again.';
    case EMAIL_ALREADY_IN_USE_CODE:
      return 'That email is already used by another account.';
    case WALLET_SIGNATURE_REQUIRED_CODE:
      return 'This change must be signed with the wallet linked to your account. Connect it and try again.';
    case WALLET_SIGNATURE_INVALID_CODE:
      return 'That signature was not accepted. Make sure your wallet is unlocked on the wallet linked to this account, then try again.';
    default:
      return getApiErrorMessage(err, fallback);
  }
};

/** `0x1234…abcd` — enough for a buyer to tell two wallets apart without wrapping the line. */
export const shortWalletAddress = (address?: string | null): string => {
  const value = (address ?? '').trim();

  if (value.length <= 12) {
    return value;
  }

  return `${value.slice(0, 6)}…${value.slice(-4)}`;
};

/** Case-insensitive address comparison; `false` unless both addresses are present. */
export const isSameWalletAddress = (a?: string | null, b?: string | null): boolean => {
  if (!a || !b) {
    return false;
  }

  return a.trim().toLowerCase() === b.trim().toLowerCase();
};
